import { message } from "antd";
import { useScenarioNode } from "hooks/useScenarioNode";
import { memo, useCallback, useMemo } from "react";
import { Scenario } from "types/scenario";
import { NODE_API_PENDING_MESSAGE } from "../constants";
import { getAncestorKeys } from "../utils";
import { FlowEditor } from "./FlowEditor";
import { NodeInspector } from "./NodeInspector";

type ScenarioEditorProps = {
  scenario: Scenario;
};

export const ScenarioEditor = memo(({ scenario }: ScenarioEditorProps) => {
  const {
    nodes,
    selectedNode,
    selectedNodeId,
    loadingNode,
    savingNode,
    startNodeKey,
    selectNode,
    addNode,
    addBranchNode,
    editNode,
    cancelNode,
    saveNode,
    deleteNode,
  } = useScenarioNode({ scenario });

  const blockedNextKeys = useMemo(() => {
    if (!selectedNode) {
      return [];
    }

    const blocked = getAncestorKeys(nodes, selectedNode.key);
    blocked.add(selectedNode.key);

    return [...blocked];
  }, [nodes, selectedNode]);

  const nodeKeyOptions = useMemo(
    () =>
      nodes
        .filter((node) => !blockedNextKeys.includes(node.key))
        .map((node) => ({ value: node.key, label: node.key })),
    [blockedNextKeys, nodes],
  );

  const handleDuplicateNode = useCallback(() => {
    message.info(NODE_API_PENDING_MESSAGE);
  }, []);

  return (
    <>
      <FlowEditor
        name={scenario.name}
        startNodeKey={startNodeKey}
        nodes={nodes}
        loading={loadingNode}
        selectedNodeId={selectedNodeId}
        onSelectNode={selectNode}
        onAddNode={addNode}
        onAddBranchNode={addBranchNode}
      />

      <NodeInspector
        node={selectedNode}
        nodes={nodes}
        nodeKeyOptions={nodeKeyOptions}
        blockedNextKeys={blockedNextKeys}
        saving={savingNode}
        onChange={editNode}
        onSave={saveNode}
        onCancel={cancelNode}
        onDuplicate={handleDuplicateNode}
        onDelete={deleteNode}
      />
    </>
  );
});

ScenarioEditor.displayName = "ScenarioEditor";
